import { useState, type FormEvent } from 'react';
import { Field } from '@hive/ui';
import { api, errorMessage, type Screen } from '../api/client';
import { Dialog } from './Dialog';

interface Props {
  screens: Screen[];
  defaultOffer?: { sponsor?: string; title?: string; body?: string };
  patronUrl?: string;
  onUpdated(screen: Screen): void;
  onClose(): void;
}

/** Launch or clear a full-screen Nectar slate on one screen. */
export function PromoDialog({ screens, defaultOffer, patronUrl, onUpdated, onClose }: Props) {
  const paired = screens.filter((s) => s.status !== 'unpaired');
  const [screenId, setScreenId] = useState(paired[0]?.id ?? '');
  const [sponsor, setSponsor] = useState(defaultOffer?.sponsor ?? '');
  const [title, setTitle] = useState(defaultOffer?.title ?? '');
  const [body, setBody] = useState(defaultOffer?.body ?? '');
  const [qrUrl, setQrUrl] = useState(patronUrl ?? 'https://');
  const [duration, setDuration] = useState('30');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const target = paired.find((s) => s.id === screenId);

  async function run(action: () => Promise<{ data?: Screen; error?: unknown }>) {
    setBusy(true);
    setError(null);
    try {
      const { data, error } = await action();
      if (!data) return setError(errorMessage(error));
      onUpdated(data);
      onClose();
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  function launch(e: FormEvent) {
    e.preventDefault();
    const seconds = Number(duration);
    void run(() =>
      api.POST('/v1/screens/{screenId}/promo', {
        params: { path: { screenId } },
        body: {
          title: title.trim(),
          qrUrl: qrUrl.trim(),
          ...(body.trim() ? { body: body.trim() } : {}),
          ...(sponsor.trim() ? { sponsorName: sponsor.trim() } : {}),
          ...(seconds >= 5 ? { durationSeconds: seconds } : {}),
        },
      }),
    );
  }

  return (
    <Dialog titleId="promo-title" title="Nectar promo" subtitle="A full-screen sponsor slate with a QR code, then back to the match" onClose={onClose} width={560}>
      <form className="k-form" onSubmit={launch}>
        {error && (
          <p className="k-inline-error" role="alert">
            {error}
          </p>
        )}
        <div className="hv-field">
          <label className="hv-field__label" htmlFor="promo-screen">
            Screen
          </label>
          <select id="promo-screen" className="hv-input" value={screenId} onChange={(e) => setScreenId(e.target.value)}>
            {paired.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.promo ? ' (slate on screen)' : ''}
              </option>
            ))}
          </select>
        </div>
        {target?.promo ? (
          <div className="k-promo-active">
            <div>
              <span className="k-promo-active__label">On screen now</span>
              <span className="k-promo-active__title">{target.promo.title}</span>
            </div>
            <button
              type="button"
              className="hv-btn hv-btn--secondary"
              disabled={busy}
              onClick={() => run(() => api.DELETE('/v1/screens/{screenId}/promo', { params: { path: { screenId } } }))}
            >
              {busy ? 'Clearing…' : 'Clear promo'}
            </button>
          </div>
        ) : (
          <>
            <Field id="promo-sponsor" label="Sponsor" value={sponsor} onChange={(e) => setSponsor(e.target.value)} maxLength={80} />
            <Field id="promo-title" label="Headline" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} required />
            <Field id="promo-body" label="Details (optional)" value={body} onChange={(e) => setBody(e.target.value)} maxLength={280} />
            <Field id="promo-qr" label="QR code link" type="url" inputMode="url" value={qrUrl} onChange={(e) => setQrUrl(e.target.value)} pattern="https?://.+" required />
            <Field id="promo-duration" label="Clear automatically after (seconds)" type="number" min={5} max={3600} value={duration} onChange={(e) => setDuration(e.target.value)} />
            <button type="submit" className="hv-btn hv-btn--primary hv-btn--lg" disabled={busy || !screenId}>
              {busy ? 'Launching…' : 'Launch promo'}
            </button>
          </>
        )}
      </form>
    </Dialog>
  );
}
